import { useDispatch, useSelector } from 'react-redux';
import { Switch } from '@rsos/components/capstone/base';
import { selectCapstoneAudioSettingsForFeature } from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { selectInteragencyChatNotifications } from '@rsos/sinatra/src/selectors/selectInteragencyChatNotifications';
import useIsIRP from '@rsos/utils/useIsIRP';
import { AdminDispatch } from '../../../../app/store';
import { usePatchAudioSettings } from '../../app/patchAudioSettings';
import { usePatchInteragencyChatNotifications } from '../../app/patchInteragencyChatNotifications';
import SoundSelect from '../SoundSelect';
import { trackToggleSoundOnOff } from '../audioSettingsTrackings';
import styles from '../settings.module.css';
import { trackToggleInteragencyBannersOnOff } from './agencyConversationsTrackings';

const FEATURE = 'interagency_chat';

/**
 * Admin card for the agency's interagency chat notifications, applied to every
 * user in the agency: a banner toggle, a sound toggle, and a tone selector.
 * Banners are not audio, so they patch separately from the sound settings.
 */
export function AgencyConversationsNotificationSettings() {
  const dispatch = useDispatch<AdminDispatch>();
  const { banner_on: isBannerOn } = useSelector(
    selectInteragencyChatNotifications,
  );
  const { sound_on: isSoundOn, tone } = useSelector(
    selectCapstoneAudioSettingsForFeature(FEATURE),
  );

  const patchInteragencyChatNotifications =
    usePatchInteragencyChatNotifications();
  const patchAudioSettings = usePatchAudioSettings(FEATURE);
  const { irpVersion } = useIsIRP();

  const toggleBannerOn = () => {
    const nextBannerOn = !isBannerOn;
    dispatch(patchInteragencyChatNotifications({ banner_on: nextBannerOn }));
    trackToggleInteragencyBannersOnOff(irpVersion, nextBannerOn);
  };

  const updateAudioSettings = (
    update: Partial<{ sound_on: boolean; tone: string }>,
  ) => {
    dispatch(patchAudioSettings(update));
  };

  const toggleSoundOn = () => {
    updateAudioSettings({ sound_on: !isSoundOn });
    trackToggleSoundOnOff(irpVersion, isSoundOn, FEATURE);
  };

  return (
    <div className={styles.card}>
      <div>
        <div className={styles.cardTitle}>Agency Conversations</div>
        <div className={styles.cardDescription}>
          Alert agents with sound or banners when there are new messages between
          agencies.
        </div>
      </div>
      <div>
        <div className={styles.inputLabel}>All Users</div>
        <div className={styles.inputDescription}>
          Configure sound and banner notifications for all users.
        </div>
      </div>
      <div className={styles.soundBlock}>
        <span>Notification Banners</span>
        <Switch
          dataName="interagency-chat-banner-toggle"
          onOffLabel
          onClick={toggleBannerOn}
          isOn={isBannerOn}
        />
      </div>
      <div className={`${styles.soundBlock} ${styles.groupSpacing}`}>
        <span>Sound Notification</span>
        <Switch
          dataName="interagency-chat-sound-toggle"
          onOffLabel
          onClick={toggleSoundOn}
          isOn={isSoundOn}
        />
      </div>
      <SoundSelect
        isSoundOn={isSoundOn}
        type={FEATURE}
        tone={tone}
        updateAudioSettings={updateAudioSettings}
      />
    </div>
  );
}
